import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { UniqueIdWrapper } from './style'
import { FormFields } from '~/components/FormFields'
import { Dimmer, Form, Icon, Loader, Message, Progress, Tab } from 'semantic-ui-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { ProgressWrapper, StyledMenuItem } from '~/pages/Dairy/CloudStorage/style';
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style';
import axios from 'axios'
import ENV from '~/environment';
import SimpleFileUpload from '~/components/SimpleFileUpload'
import { formatNumber, formatNumberWithoutFractions, getUsersBrowserData, handleErrors } from '~/utils/helpers'
import UpdatePrices from './UpdatePrices'

const CreateEditArticle: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const uniqueValidationMessage = t('Please enter unique id')

  const [isBusy, setIsBusy] = useState(false)
  const [articleOptions, setArticleOptions] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [openUpdatePricesModal, setOpenUpdatePricesModal] = useState(false)
  const { systemData: { languages } } = useAuth()
  const [loadingUniqueIdCheck, setLoadingUniqueIdCheck] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [correctUniqueId, setCorrectUniqueId] = useState(true)
  const [netPriceOnMount, setNetPriceOnMount] = useState('')

  const { handleSubmit, control, reset, setValue, watch, formState: { dirtyFields, errors }, getValues, clearErrors } = useForm({
    mode: 'onSubmit'
  })

  const [
    watchUniqueId,
    watchCustomField,
    watchUnit,
    watchConversion,
    watchNetPrice,
    watchWeightPerUnit,
    watchTaxPercentId
  ] = watch(['uniqueId', 'custom', 'unit', 'conversion', 'netPrice', 'weightPerUnit', 'saleTaxId'])

  useEffect(() => {
    fetchData()
  }, [t])

  useEffect(() => {
    if (correctUniqueId) {
      clearErrors('uniqueId')
    }
  }, [correctUniqueId])

  useEffect(() => {
    if (watchCustomField) {
      setValue('interval', '')
      setValue('conversion', 0)
      setValue('weightPerUnit', '')
    }
    setValue('netPrice', '')
  }, [watchCustomField])

  useEffect(() => {
    if (watchUnit !== 'piece') {
      setValue('conversion', 0)
      setValue('weightPerUnit', '')
    }
  }, [watchUnit])

  async function getFileUrls(id, languages) {
    for (const language of languages) {
      if (language?.image) {
        try {
          const response = await MilkMoneyAccountingService.getMilkMoneyArticleFileUrl(id, { fileName: language.image });
          language.fileEdit = {
            src: response.data,
            type: language.image.split('.')[1],
            filename: language.image
          };
        } catch (error) {
          // do nothing
        }
      }
    }
    return languages
  }

  async function getParsedLanguages(articleLanguages) {
    languages.map((language) => {
      const index = articleLanguages.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        articleLanguages.push({
          id: language.id,
          name: '',
          file: '',
          order: language.order
        })
      } else {
        articleLanguages[index].order = language.order
      }
    })
    articleLanguages.sort((a, b) => a.order - b.order)
    articleLanguages?.forEach(lang => delete lang.order)
    articleLanguages = await getFileUrls(id, articleLanguages)
    return articleLanguages
  }

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: '',
      file: ''
    }))
  }

  async function fetchData() {
    setIsBusy(true)
    try {
      const responseOptions = await MilkMoneyAccountingService.getMilkMoneyShopOptions()
      setArticleOptions(responseOptions.data)
      const formValues = getValues()
      if (!formValues.uniqueId) {
        if (id) {
          const response = await MilkMoneyAccountingService.editMilkMoneyArticle(id)
          response.data.languages = await getParsedLanguages(response.data.languages)
          reset(response.data)
          setNetPriceOnMount(response.data?.netPrice ? formatNumber(response.data?.netPrice, 5) : response.data?.netPrice)
        } else {
          reset({
            languages: getLangs()
          })
        }
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)

  }

  async function onSubmit(data) {
    if (id && dirtyFields.netPrice && !openUpdatePricesModal && Boolean(netPriceOnMount)) {
      setOpenUpdatePricesModal(true)
      return
    }
    const browserData = getUsersBrowserData()
    data.netPrice = data.netPrice ? data.netPrice.replace(',', '.') : ''
    data.weightPerUnit = data.weightPerUnit ? data.weightPerUnit.replace(',', '.') : ''
    const formData = new FormData();
    Object.keys(data).map((key, index) => {
      if (key !== 'languages') {
        const value = Object.values(data)[index]
        //@ts-ignore
        formData.append(key, value)
      }
    })
    Object.keys(browserData).map((key, index) => {
      formData.append(key, Object.values(browserData)[index]);
    })
    data.languages.forEach((language, index) => {
      Object.keys(language).map(languageKey => {
        if (languageKey !== 'file') {
          formData.append(`languages[${index}][${languageKey}]`, language[languageKey] || '')
        }
      })
      if (language.file) {
        formData.append(`image-${language.id}`, language.file);
        delete language.file
      }
    });
    const source = axios.CancelToken.source();
    const url = `/milk-money/shop/articles/${id ? `${id}/update` : 'store'}`
    setIsUploading(true)
    try {
      await axios.post(
        `${ENV.API_URL}${url}`,
        formData,
        {
          headers: { 'Authorization': localStorage.getItem('token') },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            setUploadProgress((loaded / total) * 100);
          },
          cancelToken: source.token
        });
      const successMessage = id ? t('Successfully edited article') : t('Successfully created article')
      notify(successMessage, 'success')
      navigate(RouteTypes.DAIRY_FACTORY_PROFILE_SHOP)
    } catch (error) {
      if (axios.isCancel(error)) {
        notify(t('Upload canceled'), 'error');
      } else {
        handleErrors(error)
      }
    } finally {
      setIsUploading(false)
    }
  }

  async function checkUniqueId(uniqueId) {
    setValue('uniqueId', uniqueId)
    setLoadingUniqueIdCheck(true)
    try {
      const payload = id ? { id, uniqueId } : { uniqueId }
      const response = await MilkMoneyAccountingService.checkUniqueId({ ...payload })
      setCorrectUniqueId(Boolean(response.data))
    } catch (error) {
      handleErrors(error)
    }
    setLoadingUniqueIdCheck(false)
  }

  function errorTab() {
    //@ts-ignore
    return errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  async function handleDeleteImage(fileName) {
    try {
      await MilkMoneyAccountingService.deleteMilkMoneyArticleFile(id, { fileName })
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          const langAdded = getValues(`languages[${indexLanguage}].image`) && getValues('id')
          return {
            menuItem: getMenuItem(language['label'], indexLanguage),
            pane: <StyledTabPane key={language.id}>
              <FormFields.Input
                control={control}
                width={9}
                name={`languages[${indexLanguage}].name`}
                label={t('Article Name')}
                placeholder={t('Article Name')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter article name')
                  },
                  maxLength: {
                    value: 100,
                    message: t('Max char. is 100')
                  }
                }}
              />
              <FormFields.External
                control={control}
                label={t('Image')}
                keyName={getValues(`languages[${indexLanguage}]`)}
                id={getValues('id') || `languages[${indexLanguage}].file`}
                name={langAdded ? `languages[${indexLanguage}].fileEdit` : `languages[${indexLanguage}].file`}
                onChange={(file: File) => {
                  setValue(`languages[${indexLanguage}].file`, file)
                }}
              >
                <SimpleFileUpload
                  removeButton={() => id && handleDeleteImage(getValues(`languages[${indexLanguage}].image`))}
                  accept={['.jpeg', '.jpg', '.png', '.bmp']}
                  sizeLimitInMB={5}
                />
              </FormFields.External>
            </StyledTabPane>
          }
        })}
    />
  }

  function roundToFixed(number) {
    return isNaN(parseFloat(number?.toString().replace(',', '.'))) ? '0.00' : parseFloat(number.toString().replace(',', '.')).toFixed(2)
  }

  function displayMessage() {
    return watchConversion === 1 && Boolean(watchNetPrice) && Boolean(watchTaxPercentId) && !watchCustomField && Boolean(watchWeightPerUnit)
  }

  function renderForm() {
    const tax = articleOptions?.saleTaxes?.find(tax => watchTaxPercentId === tax.id)
    //@ts-ignore
    const netPricePerKg = roundToFixed(watchNetPrice) / roundToFixed(watchWeightPerUnit)
    //@ts-ignore
    const grossPricePerKg = formatNumber(roundToFixed(netPricePerKg) * (1 + tax?.percent / 100))
    return <>
      <UniqueIdWrapper>
        <FormFields.Input
          control={control}
          name={'uniqueId'}
          disabled={Boolean(id)}
          placeholder={t('Article No.')}
          onChange={(e, data) => checkUniqueId(data.value)}
          rules={{
            required: {
              value: true,
              message: t('Please enter article no.')
            },
            maxLength: {
              value: 20,
              message: t('Max number of characters is 20')
            },
            validate: () => !watchUniqueId || watchUniqueId === '' ? true : correctUniqueId || uniqueValidationMessage
          }}
          label={t('Article No.')}
        />
        <div className='loaderWrapper'>
          {loadingUniqueIdCheck && <Dimmer inverted active={loadingUniqueIdCheck}>
            <Loader size='small' active={loadingUniqueIdCheck} />
          </Dimmer>}
        </div>
        {!loadingUniqueIdCheck && watchUniqueId !== '' && dirtyFields?.uniqueId && <Icon
          style={{ marginLeft: 10 }}
          name={correctUniqueId ? 'checkmark' : 'close'}
          color={correctUniqueId ? 'green' : 'red'}
        />}
      </UniqueIdWrapper>
      {renderLanguageFields()}
      <Form.Group widths={'equal'}>
        <FormFields.Checkbox
          control={control}
          name='status'
          defaultValue={0}
          checkboxValue={1}
          label={t('Status')}
        />
        <FormFields.Checkbox
          control={control}
          name='farmerCanAdd'
          defaultValue={0}
          checkboxValue={1}
          label={t('Farmer can add')}
        />
        <FormFields.Checkbox
          control={control}
          name='custom'
          defaultValue={0}
          checkboxValue={1}
          label={t('Custom')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          name={'unit'}
          control={control}
          placeholder={t('Select unit')}
          selectOnBlur={false}
          label={t('Unit')}
          rules={{
            required: {
              value: true,
              message: t('Please select unit')
            }
          }}
          options={articleOptions?.units?.map(unit => ({ value: unit.key, text: unit.label }))}
        />
        {watchCustomField !== 1 && <FormFields.Select
          name={'interval'}
          control={control}
          placeholder={t('Select period')}
          label={t('Period')}
          selectOnBlur={false}
          rules={{
            required: {
              value: true,
              message: t('Please select period')
            }
          }}
          options={articleOptions?.intervals?.map(interval => ({ value: interval.key, text: interval.label }))}
        />
        }
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          name={'saleTaxId'}
          control={control}
          selectOnBlur={false}
          upward
          scrolling
          placeholder={t('Select sale tax')}
          label={t('Sale tax')}
          rules={{
            required: {
              value: true,
              message: t('Please select sale tax')
            }
          }}
          options={articleOptions?.saleTaxes?.map(saleTax => ({ value: saleTax.id, text: `${saleTax.name} (${saleTax.percent} %)` }))}
        />
        <FormFields.Select
          name={'type'}
          control={control}
          placeholder={t('Select type')}
          selectOnBlur={false}
          label={t('Type')}
          rules={{
            required: {
              value: true,
              message: t('Please select type')
            }
          }}
          options={articleOptions?.types?.map(unit => ({ value: unit.key, text: unit.label }))}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        {!watchCustomField && <FormFields.Input
          control={control}
          width={8}
          suffix={' €'}
          name={'netPrice'}
          label={t('Net price (in €)')}
          placeholder={t('Enter net price')}
          typeInput={'controled'}
          allowDecimals
          decimalScale={5}
          rules={{
            required: {
              value: true,
              message: t('Please enter net price')
            }
          }}
        />}
        <FormFields.Select
          name={'invoiceType'}
          control={control}
          width={8}
          placeholder={t('Select type')}
          selectOnBlur={false}
          label={t('Billing type')}
          rules={{
            required: {
              value: true,
              message: t('Please select type')
            }
          }}
          options={articleOptions?.invoiceTypes?.map(unit => ({ value: unit.key, text: unit.label }))}
        />
      </Form.Group>
      {watchUnit === 'piece' && watchCustomField === 0 && <FormFields.Checkbox
        defaultValue={0}
        checkboxValue={1}
        label={t('Conversion to kilograms?')}
        control={control}
        name={'conversion'}
      />}
      {watchConversion === 1 && <FormFields.Input
        control={control}
        name={'weightPerUnit'}
        typeInput={'controled'}
        label={t('Weight per individual unit of measure')}
        allowDecimals
        decimalScale={3}
        rules={{
          required: {
            value: true,
            message: t('Please enter weight per unit')
          },
          validate: (value) => (parseFloat(roundToFixed(value)) > 0.001 && parseFloat(roundToFixed(value)) < 999.999) || t('Min value is 0,001 and max is 999,999')
        }}
      />}
      {displayMessage() && <Message info>
        <p>{t('Net price per kilogram (in €) = {{netPricePerKg}}', { netPricePerKg: formatNumber(netPricePerKg) })}</p>
        <p>{t('Gross price per kilogram (in €) = {{grossPricePerKg}}', { grossPricePerKg })}</p>
      </Message>
      }
    </>
  }

  function renderProgressBar() {
    const uploadPercent = `${formatNumberWithoutFractions(uploadProgress)}%`
    return <ProgressWrapper style={{ width: 400 }}>
      <Progress percent={uploadProgress} indicating className='custom-progress' />
      <p className='text-below-progress'>{t('Uploaded: {{uploadPercent}}', { uploadPercent })}</p>
    </ProgressWrapper>
  }

  function renderUpdatePricesModal() {
    return <UpdatePrices
      isDialogOpen={openUpdatePricesModal}
      handleClose={() => setOpenUpdatePricesModal(false)}
      control={control}
      oldValue={netPriceOnMount}
      isUploading={isUploading}
      handleSubmit={() => onSubmit(getValues())}
      article={getValues()}
    />
  }

  return <Layout
    title={id ? t('Edit article') : t('Create article')}
    useSpinner
    fontSizeHeader={'18px'}
    onSubmit={handleSubmit(onSubmit)}
    childContainerSize={'100vh'}
    action={id ? t('Update') : t('Save article')}
    isBusy={isBusy || isUploading}
    close={RouteTypes.DAIRY_FACTORY_PROFILE_SHOP}
  >
    {openUpdatePricesModal && renderUpdatePricesModal()}
    <Dimmer inverted active={isUploading && uploadProgress > 0}>
      {isUploading && uploadProgress > 0 && renderProgressBar()}
    </Dimmer>
    {!isBusy && renderForm()}
  </Layout>
}
export default CreateEditArticle